import React, { useState, useCallback } from 'react';
import { useLocalization } from '../hooks/useLocalization';

interface CyrillicInputProps {
  /** Input value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether input is required */
  required?: boolean;
  /** Maximum length */
  maxLength?: number;
  /** Additional CSS classes */
  className?: string;
  /** Input type */
  type?: 'text' | 'textarea';
  /** Number of rows for textarea */
  rows?: number;
  /** Whether to show validation errors */
  showValidation?: boolean;
  /** Custom validation function */
  customValidator?: (value: string) => string | null;
}

/**
 * Input component with Cyrillic text validation and normalization
 * Requirements: 12.1, 12.2, 12.5
 */
export const CyrillicInput: React.FC<CyrillicInputProps> = ({
  value,
  onChange,
  placeholder = '',
  required = false,
  maxLength,
  className = '',
  type = 'text',
  rows = 3,
  showValidation = true,
  customValidator
}) => {
  const { validateCyrillicText, normalizeCyrillicText, t } = useLocalization();
  const [touched, setTouched] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateInput = useCallback((inputValue: string) => {
    // Required validation
    if (required && !inputValue.trim()) {
      return 'Это поле обязательно для заполнения';
    }

    // Length validation
    if (maxLength && inputValue.length > maxLength) {
      return `Максимальная длина: ${maxLength} символов`;
    }

    // Cyrillic validation
    if (inputValue.trim() && !validateCyrillicText(inputValue)) {
      return 'Используйте только русские буквы, цифры и знаки препинания';
    }

    // Custom validation
    if (customValidator) {
      const customError = customValidator(inputValue);
      if (customError) {
        return customError;
      }
    }

    return null;
  }, [required, maxLength, validateCyrillicText, customValidator]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    
    // Normalize the text
    const normalizedValue = normalizeCyrillicText(newValue);
    
    // Validate
    const error = validateInput(normalizedValue);
    setValidationError(error);
    
    // Call onChange with normalized value
    onChange(normalizedValue);
  };

  const handleBlur = () => {
    setTouched(true);
    const error = validateInput(value);
    setValidationError(error);
  };

  const handleFocus = () => {
    if (!touched) {
      setValidationError(null);
    }
  };

  const hasError = showValidation && touched && validationError;
  const inputClassName = `
    ${className}
    ${hasError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}
    block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1
  `.trim();

  const commonProps = {
    value,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    placeholder,
    maxLength,
    className: inputClassName,
    'aria-invalid': hasError ? 'true' : 'false',
    'aria-describedby': hasError ? 'input-error' : undefined
  };

  return (
    <div className="cyrillic-input">
      {type === 'textarea' ? (
        <textarea
          {...commonProps}
          rows={rows}
        />
      ) : (
        <input
          {...commonProps}
          type="text"
        />
      )}
      
      {/* Character count */}
      {maxLength && (
        <div className="mt-1 text-xs text-gray-500 text-right">
          {value.length}/{maxLength}
        </div>
      )}
      
      {/* Validation error */}
      {hasError && (
        <div id="input-error" className="mt-1 text-sm text-red-600">
          {validationError}
        </div>
      )}
      
      {/* Validation success */}
      {showValidation && touched && !validationError && value.trim() && (
        <div className="mt-1 text-sm text-green-600">
          ✓ Корректный ввод
        </div>
      )}
    </div>
  );
};

export default CyrillicInput;